import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "@/composables/refresh-on-entity-change";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { useOrganizationManager } from "./organization-manager";
import { registerFlush } from "./pending-flush";
import { clearStaleContentOnSwitch } from "./stale-while-revalidate";
import { isAbortError, useLatestRequest } from "./use-latest-request";

export type Domain = types.Domain;

// Uses the current route to determine what the 'domain' should be and fetches
// the domain description for you.
export const useDomainStore = defineEntityStore("domain", () => {
    const router = useRouter();
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const syncStore = useSyncStore();
    const entityId = computed(() => domain.value?.identity);
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.DOMAIN,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.DOMAIN);

    const { domain: domainInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetching = ref(false);
    const isFetchingDescription = ref(false);

    const domain: Ref<Domain | undefined> = ref(undefined);
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(domain);
    // Latest-request guard: each fetchDomain gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();

    async function fetchDomain(force = false) {
        const domainId = domainInfo.value?.identity;

        // In case the same domain is selected, return early.
        if (!force && domain.value && domain.value.identity === domainId) {
            return;
        }

        // Start a new request: aborts the previous one, our own signal + token.
        const { token, signal } = begin();

        if (!domainId) {
            description.value = undefined;
            domain.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded domain + description
        // on screen while the *same* domain is refetched, so it doesn't blank the
        // page (the deferred spinner then never flashes). The data overwrites
        // below; fetchDescription always refetches for the current domain.
        // Switching to a *different* domain is handled by clearStaleContentOnSwitch.

        try {
            const { response, data } =
                await api.startDomainDomainMgtGetDomainStartPost({
                    body: {
                        domain_id: `${domainId}`,
                    },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value!.name,
                            stekName: stek.value!.name,
                        },
                        query: { type: "domain", id: domainId },
                    });
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-domain refetch is dropped; navigating to a
            // different domain must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                domainId === domain.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            domain.value = data;
            // The fetched row is the new name baseline. Without this, a
            // server-side rename surfacing on a same-domain refetch would
            // read as a local edit (the name manager only re-baselines on
            // identity change) and get echoed back as a save.
            persistName();

            // Fetch description on the same signal/token so an abort or a newer
            // request supersedes it too, and isFetching stays true until it's done.
            if (domain.value) {
                await fetchDescription(signal, token);
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the fetch flag.
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }
    // Drop the previous domain's content when the route switches to a different
    // domain, so stale-while-revalidate never renders the old one on the new page.
    clearStaleContentOnSwitch(
        () => domainInfo.value?.identity,
        [domain, description]
    );
    watch(
        () => domainInfo.value?.identity,
        () => fetchDomain(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (domain.value) fetchDomain(true);
        }
    );

    const fetchDescription = async (signal: AbortSignal, token: number) => {
        // Always refetch for the (possibly new) current domain — under SWR the
        // previous description stays set, so a `!description.value` guard would
        // wrongly keep the stale value.
        if (domain.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    domain.value,
                    "domainDescription",
                    signal,
                    revisionQuery.value
                );

                // A newer request superseded us — commit nothing. Same for
                // edits that appeared while fetching (landing-time guard).
                if (
                    !isCurrent(token) ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                if (isCurrent(token)) {
                    isFetchingDescription.value = false;
                }
            }
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() =>
        Boolean(isNameModified.value || description.value?.isModified)
    );

    // One shared opts object: the notification guards and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.DOMAIN,
        currentEntityId: () => domain.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => fetchDomain(true),
    };

    refreshOnExternalEntityChange(externalRefresh);

    const queueSave = async () => {
        if (!isModified.value || !domain.value) {
            return;
        }

        const updateContent: types.StartDomainDomainMgtUpdateDomainStartPostData =
            {
                body: {
                    domain_id: domain.value.identity,
                },
                url: "/domain/domain_mgt/update_domain/start",
            };
        if (isNameModified.value) {
            updateContent.body.name = domain.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.domain_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            // Only domain_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.DOMAIN,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    return {
        domain,
        description,
        isFetching: computed(
            () => isFetching.value || isFetchingDescription.value
        ),
        isModified,
        reset,
    };
});

export const useDomainManager = () => storeToRefs(useDomainStore());
